
import { supabase } from "@/integrations/supabase/client";
import { fetchCategoryMappings, resolveCategoryId } from "./categoryMappingUtils";
import { ImportResult, ParsedPrompt } from "./importTypes";

/**
 * Parses markdown content into prompt objects with improved handling of whitespace and formatting
 */
export function parseMarkdownPrompts(content: string): ParsedPrompt[] {
  const promptsData: ParsedPrompt[] = [];

  // Split content by prompt delimiter (looking for markdown headers)
  const promptSections = content.split(/(?=^# )/m).filter(section => section.trim());

  for (const section of promptSections) {
    try {
      const lines = section.split('\n');
      
      // Extract title from header
      const titleMatch = lines[0].match(/^# (.+)$/);
      const title = titleMatch ? titleMatch[1].trim() : '';
      
      // Initialize fields
      let description = '';
      let promptContent = '';
      let category = '';
      let source = '';
      
      // Improved parsing that handles different formatting styles
      let currentSection = '';
      let inCodeBlock = false;
      
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        
        // Check for section headers
        const sectionMatch = line.match(/^## (Description|Prompt|Prompt Content|Category|Source)$/i);
        
        if (sectionMatch && !inCodeBlock) {
          currentSection = sectionMatch[1].toLowerCase();
          continue;
        }
        
        // Handle code block markers
        if (line === '```') {
          inCodeBlock = !inCodeBlock;
          if (currentSection === 'prompt' || currentSection === 'prompt content') {
            promptContent += line + '\n';
          }
          continue;
        }
        
        // Process line based on current section
        if (line === '' && !inCodeBlock) {
          // Skip empty lines unless in content or code block
          if (currentSection === 'prompt' || currentSection === 'prompt content') {
            promptContent += '\n';
          }
          continue;
        }
        
        switch (currentSection) {
          case 'description':
            description += (description ? '\n' : '') + line;
            break;
          
          case 'prompt':
          case 'prompt content':
            promptContent += (promptContent ? '\n' : '') + line;
            break;
          
          case 'category':
            // Only set category if it's empty or the line contains content
            if (!category && line) {
              category = line;
            }
            break;
          
          case 'source':
            if (!source && line) {
              source = line;
            }
            break;
        }
      }
      
      // Clean up fields
      description = description.trim();
      promptContent = promptContent.trim();
      category = category.trim();
      source = source.trim();
      
      // Add to prompts array if we have the minimum required fields
      if (title) {
        promptsData.push({
          title,
          description,
          content: promptContent,
          category,
          source
        });
      }
    } catch (error) {
      console.error('Error parsing prompt section:', error);
      // Continue with the next section
    }
  }

  return promptsData;
}

/**
 * Imports prompts from Markdown file
 */
export const importPromptsFromMarkdown = async (
  content: string,
  userId: string
): Promise<ImportResult> => {
  try {
    const categoryMapping = await fetchCategoryMappings();

    // Parse markdown content into prompt objects
    const promptsData = parseMarkdownPrompts(content);
    
    const result: ImportResult = {
      success: false,
      imported: 0,
      failed: 0,
      errors: []
    };
    
    const promptsToInsert = [];
    
    for (const promptData of promptsData) {
      try {
        // Validate required fields
        if (!promptData.title || !promptData.description || !promptData.content) {
          result.errors.push(`Missing required fields for prompt: ${promptData.title || 'Unnamed prompt'}`);
          result.failed++;
          continue;
        }
        
        let categoryId = resolveCategoryId(promptData.category, categoryMapping);
        
        if (!categoryId) {
          result.errors.push(`Could not find category match for: ${promptData.category} in prompt: ${promptData.title}`);
          result.failed++;
          continue;
        }
        
        promptsToInsert.push({
          title: promptData.title,
          description: promptData.description,
          content: promptData.content,
          category_id: categoryId,
          user_id: userId,
          source: promptData.source || null,
          status: 'pending'
        });
      } catch (err: any) {
        result.errors.push(`Error processing prompt ${promptData.title || 'Unnamed prompt'}: ${err.message}`);
        result.failed++;
      }
    }
    
    const batchSize = 10;
    for (let i = 0; i < promptsToInsert.length; i += batchSize) {
      const batch = promptsToInsert.slice(i, i + batchSize);
      const { error } = await supabase.from('prompts').insert(batch);
      
      if (error) {
        result.errors.push(`Error inserting batch ${i/batchSize + 1}: ${error.message}`);
        result.failed += batch.length;
      } else {
        result.imported += batch.length;
      }
    }
    
    result.success = result.imported > 0;
    return result;
    
  } catch (error: any) {
    console.error('Error importing prompts from markdown:', error);
    return {
      success: false,
      imported: 0,
      failed: 0,
      errors: [error.message]
    };
  }
};
